import { clientAccess } from '@/services/clientAccess';
import type { Client } from '@/types';

export interface PublicDemoStats {
  clientName: string;
  todayCostUsd: number;
  dailyLimitUsd: number;
  thirtyDayMessages: number;
  thirtyDayUniqueConversations: number;
  thirtyDayCostUsd: number;
}

type CounterRow = Record<string, unknown>;

/**
 * The public demo's counters for the last 30 days, read through the client
 * access Lambda (TASK-625): cost, conversations and unique conversations, one
 * row per day each.
 */
async function fetchStatsForClient(client: Client): Promise<PublicDemoStats> {
  const { publicDemoDailyLimitUsd } = client.config;
  const { today, cost, conversations, uniqueConversations } = await clientAccess<{
    today: string;
    cost: CounterRow[];
    conversations: CounterRow[];
    uniqueConversations: CounterRow[];
  }>('publicDemo.stats', { clientName: client.name });

  // Aggregate cost data
  let thirtyDayCostUsd = 0;
  let todayCostUsd = 0;
  const todaySk = `COST#DATE#${today}`;

  for (const item of cost) {
    const c = Number(item.accumulated_cost_usd ?? 0);
    thirtyDayCostUsd += c;
    if (item.SK === todaySk) {
      todayCostUsd = c;
    }
  }

  // Aggregate message counts
  let thirtyDayMessages = 0;
  for (const item of conversations) {
    thirtyDayMessages += Number(item.conversation_count ?? 0);
  }

  // Aggregate unique conversation counts
  let thirtyDayUniqueConversations = 0;
  for (const item of uniqueConversations) {
    thirtyDayUniqueConversations += Number(item.unique_count ?? 0);
  }

  return {
    clientName: client.name,
    todayCostUsd: Math.round(todayCostUsd * 100) / 100,
    dailyLimitUsd: publicDemoDailyLimitUsd ?? 50,
    thirtyDayMessages,
    thirtyDayUniqueConversations,
    thirtyDayCostUsd: Math.round(thirtyDayCostUsd * 100) / 100,
  };
}

export async function fetchPublicDemoStats(clients: Client[]): Promise<PublicDemoStats[]> {
  const demoClients = clients.filter((c) => c.config.publicDemo);
  if (demoClients.length === 0) return [];

  const results = await Promise.allSettled(demoClients.map(fetchStatsForClient));

  return results
    .filter((r): r is PromiseFulfilledResult<PublicDemoStats> => r.status === 'fulfilled')
    .map((r) => r.value);
}
